/**
 * @fileoverview Componente responsável por reproduzir mensagens de áudio na interface.
 */

import React, { useState, useRef, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import PlayArrowRoundedIcon from "@material-ui/icons/PlayArrowRounded";
import PauseRoundedIcon from "@material-ui/icons/PauseRounded";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";

const useStyles = makeStyles((theme) => ({
  audioPlayerContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 280,
    maxWidth: 420,
    padding: "12px 10px 8px 10px",
    gap: 8,
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  playButton: {
    width: 40,
    height: 40,
    padding: 0,
    background: "linear-gradient(135deg, #5b7cff 0%, #4a6eef 100%)",
    color: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(135deg, #4a6eef 0%, #3960df 100%)",
    },
  },
  playIcon: {
    fontSize: 24,
  },
  waveformContainer: {
    flex: 1,
    position: "relative",
    height: 40,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 2,
    paddingLeft: 4,
    paddingRight: 4,
  },
  fadeLeft: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 20,
    pointerEvents: "none",
    zIndex: 2,
  },
  fadeRight: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 20,
    pointerEvents: "none",
    zIndex: 2,
  },
  waveformBar: {
    flex: 1,
    height: "100%",
    backgroundColor: "transparent",
    borderRadius: 2,
    transition: "all 0.15s ease",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  waveformBarInner: {
    width: "100%",
    backgroundColor: theme.palette.type === "light" ? "#d0d0d5" : "#5a5a5f",
    borderRadius: 2,
    transition: "all 0.15s ease",
  },
  downloadButton: {
    width: 32,
    height: 32,
    padding: 0,
    color: theme.palette.type === "light" ? "#5f6368" : "#9aa0a6",
    transition: "all 0.25s ease",
    "&:hover": {
      color: "#5b7cff",
      backgroundColor: "rgba(91, 124, 255, 0.08)",
      transform: "scale(1.08)",
    },
  },
  downloadIcon: {
    fontSize: 20,
  },
  metadataRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: 4,
    paddingRight: 4,
  },
  timeDisplay: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.type === "light" ? "#5f6368" : "#9aa0a6",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    letterSpacing: "0.3px",
  },
  speedControl: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.type === "light" ? "#5b7cff" : "#7a98ff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 12,
    transition: "all 0.2s ease",
    userSelect: "none",
    backgroundColor: "rgba(91, 124, 255, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(91, 124, 255, 0.15)",
      transform: "scale(1.05)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
}));

// Gera forma de onda simulada (alturas aleatórias mas consistentes)
const generateWaveform = (audioUrl, barCount = 40) => {
  // Usa a URL como seed para gerar sempre a mesma forma de onda para o mesmo áudio
  const seed = audioUrl.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const bars = [];

  for (let i = 0; i < barCount; i++) {
    // Gera altura pseudo-aleatória baseada no seed e posição
    const random = Math.sin(seed + i * 1.5) * 10000;
    const height = (Math.abs(random) % 60) + 20; // Entre 20% e 80%
    bars.push(height);
  }

  return bars;
};

const formatTime = (seconds) => {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const AudioPlayer = ({ audioUrl, isDeleted = false, fromMe = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [waveform] = useState(() => generateWaveform(audioUrl));

  // Determina as cores do fade baseado no tema e se a mensagem é do usuário
  const getFadeColors = () => {
    const isLight = theme.palette.type === "light";
    if (fromMe) {
      // Mensagem enviada (verde)
      return isLight ? "220, 248, 198" : "39, 77, 55"; // #DCF8C6 ou #274D37
    } else {
      // Mensagem recebida (branco/cinza)
      return isLight ? "255, 255, 255" : "36, 38, 38"; // #ffffff ou #242626
    }
  };

  const fadeColors = getFadeColors();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleWaveformClick = (event) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const percentage = x / bounds.width;
    audio.currentTime = percentage * duration;
  };

  const cyclePlaybackRate = () => {
    const rates = [1.0, 1.5, 2.0];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = audioUrl;
    link.download = `audio_${Date.now()}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const mediaStyle = isDeleted ? { opacity: 0.5, filter: "grayscale(50%)" } : {};

  return (
    <div className={classes.audioPlayerContainer} style={mediaStyle}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className={classes.controlsRow}>
        {/* Play/Pause Button */}
        <IconButton
          className={classes.playButton}
          onClick={togglePlayPause}
          disabled={isDeleted}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <PauseRoundedIcon className={classes.playIcon} />
          ) : (
            <PlayArrowRoundedIcon className={classes.playIcon} />
          )}
        </IconButton>

        {/* Waveform */}
        <div
          className={classes.waveformContainer}
          onClick={handleWaveformClick}
        >
          {/* Fade esquerdo */}
          <div
            className={classes.fadeLeft}
            style={{
              background: `linear-gradient(to right, rgba(${fadeColors},1), rgba(${fadeColors},0))`
            }}
          />
          {waveform.map((height, index) => {
            const barProgress = (index / waveform.length) * 100;
            const isPlayed = barProgress < progress;
            return (
              <div key={index} className={classes.waveformBar}>
                <div
                  className={classes.waveformBarInner}
                  style={{
                    height: `${height}%`,
                    backgroundColor: isPlayed ? "#5b7cff" : undefined,
                  }}
                />
              </div>
            );
          })}
          {/* Fade direito */}
          <div
            className={classes.fadeRight}
            style={{
              background: `linear-gradient(to left, rgba(${fadeColors},1), rgba(${fadeColors},0))`
            }}
          />
        </div>

        {/* Download Button */}
        <Tooltip title="Baixar áudio" arrow>
          <IconButton
            className={classes.downloadButton}
            onClick={handleDownload}
            disabled={isDeleted}
            aria-label="Download"
          >
            <GetAppRoundedIcon className={classes.downloadIcon} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Metadata Row */}
      <div className={classes.metadataRow}>
        <span className={classes.timeDisplay}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <Tooltip title="Alterar velocidade" arrow>
          <span
            className={classes.speedControl}
            onClick={cyclePlaybackRate}
            role="button"
            tabIndex={0}
          >
            {playbackRate}x
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default AudioPlayer;